import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  DollarSign, Users, TrendingUp, TrendingDown, PieChart, BarChart3, 
  Calendar, ChevronLeft, ChevronRight, Building2, AlertCircle, CheckCircle,
  Download, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const PayrollSummary = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [employeeRanking, setEmployeeRanking] = useState([]);
  
  // Period navigation
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentHalf, setCurrentHalf] = useState(new Date().getDate() <= 15 ? 'first' : 'second');

  // Calculate current period
  const getCurrentPeriod = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    
    if (currentHalf === 'first') {
      return {
        start: `${year}-${String(month).padStart(2, '0')}-01`,
        end: `${year}-${String(month).padStart(2, '0')}-15`
      };
    } else {
      const lastDay = new Date(year, month, 0).getDate();
      return {
        start: `${year}-${String(month).padStart(2, '0')}-16`,
        end: `${year}-${String(month).padStart(2, '0')}-${lastDay}`
      };
    }
  };

  const period = getCurrentPeriod();

  const navigatePeriod = (direction) => {
    if (direction === 'prev') {
      if (currentHalf === 'first') {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
        setCurrentHalf('second');
      } else {
        setCurrentHalf('first');
      }
    } else {
      if (currentHalf === 'second') {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
        setCurrentHalf('first');
      } else {
        setCurrentHalf('second');
      }
    }
  };

  const formatPeriodLabel = () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const month = monthNames[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    
    if (currentHalf === 'first') {
      return `${month} 1-15, ${year}`;
    } else {
      const lastDay = new Date(year, currentDate.getMonth() + 1, 0).getDate();
      return `${month} 16-${lastDay}, ${year}`;
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [currentDate, currentHalf]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, trendRes, rankingRes] = await Promise.all([
        axios.get(`${API}/api/payslips/summary/dashboard?period_start=${period.start}&period_end=${period.end}`),
        axios.get(`${API}/api/payslips/summary/trend?periods=6`),
        axios.get(`${API}/api/payslips/summary/employee-ranking?period_start=${period.start}&period_end=${period.end}&limit=10`)
      ]);
      
      setDashboardData(dashboardRes.data);
      setTrendData(trendRes.data);
      setEmployeeRanking(rankingRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load payroll summary');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₱${(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  // Prepare deductions chart data
  const deductionsChartData = dashboardData?.deductions_breakdown ? 
    Object.entries(dashboardData.deductions_breakdown)
      .filter(([key, value]) => value > 0)
      .map(([key, value]) => ({
        name: key.toUpperCase().replace('_', ' '),
        value: value
      })) : [];

  const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Payroll Summary</h1>
          <p className="text-slate-500">Analytics and insights for payroll management</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadDashboardData} disabled={loading}>
            <RefreshCw size={18} className={loading ? 'animate-spin mr-2' : 'mr-2'} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Period Navigator */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigatePeriod('prev')}>
              <ChevronLeft size={20} />
            </Button>
            <div className="flex items-center gap-2">
              <Calendar size={20} className="text-blue-600" />
              <span className="text-lg font-semibold">{formatPeriodLabel()}</span>
            </div>
            <Button variant="ghost" onClick={() => navigatePeriod('next')}>
              <ChevronRight size={20} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Gross Pay</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData?.current_period?.total_gross_pay)}</p>
              </div>
              <DollarSign size={40} className="text-blue-200" />
            </div>
            {dashboardData?.previous_period_comparison && (
              <div className={`mt-2 text-sm flex items-center gap-1 ${dashboardData.previous_period_comparison.change_gross >= 0 ? 'text-green-200' : 'text-red-200'}`}>
                {dashboardData.previous_period_comparison.change_gross >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                {Math.abs(dashboardData.previous_period_comparison.change_gross_percent)}% vs previous
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Total Net Pay</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData?.current_period?.total_net_pay)}</p>
              </div>
              <DollarSign size={40} className="text-green-200" />
            </div>
            {dashboardData?.previous_period_comparison && (
              <div className={`mt-2 text-sm flex items-center gap-1 ${dashboardData.previous_period_comparison.change_net >= 0 ? 'text-green-200' : 'text-red-200'}`}>
                {dashboardData.previous_period_comparison.change_net >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                {Math.abs(dashboardData.previous_period_comparison.change_net_percent)}% vs previous
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Total Deductions</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData?.current_period?.total_deductions)}</p>
              </div>
              <TrendingDown size={40} className="text-red-200" />
            </div>
            {dashboardData?.previous_period_comparison && (
              <div className={`mt-2 text-sm flex items-center gap-1 ${dashboardData.previous_period_comparison.change_deductions >= 0 ? 'text-red-200' : 'text-green-200'}`}>
                {dashboardData.previous_period_comparison.change_deductions >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                {Math.abs(dashboardData.previous_period_comparison.change_deductions_percent || 0)}% vs previous
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Employees</p>
                <p className="text-2xl font-bold">{dashboardData?.current_period?.total_employees || 0}</p>
              </div>
              <Users size={40} className="text-purple-200" />
            </div>
            <div className="mt-2 text-sm text-purple-100">
              Avg: {formatCurrency(dashboardData?.current_period?.average_net_pay)}
              {dashboardData?.previous_period_comparison?.change_employees !== 0 && (
                <span className="ml-2 opacity-80">
                  ({dashboardData.previous_period_comparison.change_employees > 0 ? '+' : ''}{dashboardData.previous_period_comparison.change_employees})
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Period Comparison Detail */}
      {dashboardData?.previous_period_comparison && (
        <Card className="bg-slate-50 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <BarChart3 size={18} />
              Period-over-Period Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-slate-500 text-xs mb-1">Gross Pay Change</p>
                <p className={`font-semibold ${dashboardData.previous_period_comparison.change_gross >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {dashboardData.previous_period_comparison.change_gross >= 0 ? '+' : ''}{formatCurrency(dashboardData.previous_period_comparison.change_gross)}
                </p>
                <p className="text-xs text-slate-400">({dashboardData.previous_period_comparison.change_gross_percent}%)</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-slate-500 text-xs mb-1">Net Pay Change</p>
                <p className={`font-semibold ${dashboardData.previous_period_comparison.change_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {dashboardData.previous_period_comparison.change_net >= 0 ? '+' : ''}{formatCurrency(dashboardData.previous_period_comparison.change_net)}
                </p>
                <p className="text-xs text-slate-400">({dashboardData.previous_period_comparison.change_net_percent}%)</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-slate-500 text-xs mb-1">Deductions Change</p>
                <p className={`font-semibold ${dashboardData.previous_period_comparison.change_deductions <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {dashboardData.previous_period_comparison.change_deductions >= 0 ? '+' : ''}{formatCurrency(dashboardData.previous_period_comparison.change_deductions)}
                </p>
                <p className="text-xs text-slate-400">({dashboardData.previous_period_comparison.change_deductions_percent}%)</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-slate-500 text-xs mb-1">Previous Period</p>
                <p className="font-semibold text-slate-700">{dashboardData.previous_period_comparison.employee_count} employees</p>
                <p className="text-xs text-slate-400">{formatCurrency(dashboardData.previous_period_comparison.total_net)} net</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payroll Trend Chart */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp size={20} className="text-blue-600" />
              Payroll Trend (Last 6 Periods)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorNetPay" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorGrossPay" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{fontSize: 11}} stroke="#94a3b8" />
                  <YAxis tick={{fontSize: 11}} stroke="#94a3b8" tickFormatter={(v) => `₱${(v/1000).toFixed(0)}k`} />
                  <Tooltip 
                    formatter={(value) => [`₱${value.toLocaleString()}`, '']}
                    contentStyle={{borderRadius: '8px', border: '1px solid #e2e8f0'}}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="gross_pay" name="Gross Pay" stroke="#3b82f6" fill="url(#colorGrossPay)" strokeWidth={2} />
                  <Area type="monotone" dataKey="net_pay" name="Net Pay" stroke="#22c55e" fill="url(#colorNetPay)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                No trend data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Deductions Breakdown Pie Chart */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart size={20} className="text-red-600" />
              Deductions Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {deductionsChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={deductionsChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({name, value}) => `${name}: ₱${value.toLocaleString()}`}
                    labelLine={false}
                  >
                    {deductionsChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`₱${value.toLocaleString()}`, '']} />
                </RechartsPieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                No deductions data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Department Breakdown & Employee Ranking */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Department Breakdown */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 size={20} className="text-purple-600" />
              Breakdown by Company
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData?.department_breakdown?.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.department_breakdown.map((dept, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-slate-800">{dept.name}</span>
                      <span className="text-sm text-slate-500">{dept.employees} employees</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-slate-500">Gross</span>
                        <p className="font-medium text-blue-600">{formatCurrency(dept.gross_pay)}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Deductions</span>
                        <p className="font-medium text-red-600">{formatCurrency(dept.deductions)}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Net</span>
                        <p className="font-medium text-green-600">{formatCurrency(dept.net_pay)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-slate-400 py-8">No department data available</div>
            )}
          </CardContent>
        </Card>

        {/* Employee Ranking */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users size={20} className="text-green-600" />
              Top Employees by Net Pay
            </CardTitle>
          </CardHeader>
          <CardContent>
            {employeeRanking.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2">#</th>
                      <th className="text-left p-2">Employee</th>
                      <th className="text-right p-2">Net Pay</th>
                      <th className="text-center p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeRanking.map((emp) => (
                      <tr key={emp.rank} className="border-b border-slate-100">
                        <td className="p-2 font-semibold text-slate-500">{emp.rank}</td>
                        <td className="p-2">{emp.employee_name}</td>
                        <td className="p-2 text-right font-medium text-green-600">{formatCurrency(emp.net_pay)}</td>
                        <td className="p-2 text-center">
                          {emp.status === 'signed' ? (
                            <CheckCircle size={16} className="inline text-green-500" />
                          ) : emp.status === 'finalized' ? (
                            <AlertCircle size={16} className="inline text-amber-500" />
                          ) : (
                            <span className="text-xs text-slate-400">Draft</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-slate-400 py-8">No employee data available</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payslip Status Summary */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg">Payslip Status Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-3xl font-bold text-slate-600">{dashboardData?.payslips_by_status?.draft || 0}</p>
              <p className="text-sm text-slate-500">Draft</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-3xl font-bold text-amber-600">{dashboardData?.payslips_by_status?.finalized || 0}</p>
              <p className="text-sm text-amber-700">Finalized</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{dashboardData?.payslips_by_status?.signed || 0}</p>
              <p className="text-sm text-green-700">Signed</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayrollSummary;
